import React from "react";
import ObjectSelect, { TObjectFieldClassWithName } from "./ObjectSelect";
import API from "../lib/TimeEditAPI";
import Language from "../lib/Language";
import _ from "underscore";
import { TimeEdit } from "../lib/TimeEdit";
import Log from "../lib/Log";
import { User } from "../models/User";
import { TTypeIdClass, TObjectIdClass } from "../types/api/APIClassTypes";

const flexParent: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
};

const verticalFlex: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
};

const horizontalFlex: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "300px",
};

const leftList = {
    flexGrow: "1",
    marginRight: "10px",
};

const flexGrow = {
    flexGrow: "1",
};

type TExceptionEditorProps = {
    reservationIds: number[];
    onClose: () => void;
    user: User | null | undefined;
};

/**
 * A member object, as the member APIs answer with them and as ObjectSelect hands
 * them back from a click.
 */
/** getMemberObjectsForReservations answers plain object ids, with no fields. */
type TMemberObject = TObjectIdClass;

/**
 * An exception being added, with the member-type lists as they stood when it was
 * added; onSave flattens them back out.
 */
type TAddedException = {
    exception: TMemberObject;
    types: TMemberType[][];
};

/**
 * A member type. The picker takes one as its selectedType, which is a
 * TTypeIdClass, and the selects render its name.
 */
type TMemberType = TTypeIdClass & { name: string };

type TExceptionEditorState = {
    /**
     * The two lists getMemberTypesForReservations answers with: the group types,
     * and the member types available under each of them, by the same index.
     */
    parentMemberTypes: TMemberType[];
    childMemberTypes: TMemberType[][];
    selectedGroupType: number | null;
    selectedMemberType?: number;
    /** Never assigned; only the constructor's null reaches it. */
    membershipType: number | null;
    /** The members of the selected group type, and the exceptions among them. */
    membershipObjects: TMemberObject[];
    exceptions: TMemberObject[];
    selectedObjects: TMemberObject[];
    addExceptions: TAddedException[];
    removeExceptions: TMemberObject[];
    /** Read to disable the member picker, but nothing ever sets it. */
    removeAllOfType?: boolean;
};

class ExceptionEditor extends React.Component<TExceptionEditorProps, TExceptionEditorState> {
    constructor(props) {
        super(props);
        this.state = {
            selectedGroupType: null,
            selectedMemberType: TimeEdit.rootType,
            membershipType: null,
            selectedObjects: [],
            parentMemberTypes: [],
            childMemberTypes: [],
            membershipObjects: [],
            exceptions: [],
            addExceptions: [],
            removeExceptions: [],
        };
    }

    componentDidMount() {
        API.getMemberTypesForReservations(this.props.reservationIds, (result) => {
            this.setState(result);
        });
    }

    componentDidUpdate(prevProps, prevState) {
        const { selectedGroupType, selectedMemberType } = this.state;
        if (selectedGroupType && selectedMemberType) {
            if (
                prevState.selectedGroupType !== selectedGroupType ||
                prevState.selectedMemberType !== selectedMemberType
            ) {
                API.getMemberObjectsForReservations(
                    this.props.reservationIds,
                    selectedGroupType,
                    selectedMemberType,
                    (result) => {
                        API.getMemberExceptionObjectsForReservations(
                            this.props.reservationIds,
                            selectedMemberType,
                            (exceptions) => {
                                this.setState({ membershipObjects: result, exceptions });
                            }
                        );
                    }
                );
            }
        }
    }

    handleAddException(exception: TMemberObject) {
        const typedException = { exception, types: this.state.childMemberTypes };
        this.setState({
            addExceptions: this.state.addExceptions.concat(typedException),
            removeExceptions: this.state.removeExceptions.filter((re) => re.id !== exception.id),
            selectedObjects: this.state.selectedObjects.concat(exception),
        });
    }

    handleRemoveException(exception: TMemberObject) {
        this.setState({
            addExceptions: this.state.addExceptions.filter(
                (aE) => aE.exception.id !== exception.id
            ),
            removeExceptions: this.state.removeExceptions.concat(exception),
            selectedObjects: this.state.selectedObjects.filter((sO) => sO.id !== exception.id),
        });
    }

    onGroupTypeSelected(event) {
        this.setState({ selectedGroupType: parseInt(event.target.value, 10) });
    }

    onMemberTypeSelected(event) {
        this.setState({ selectedMemberType: parseInt(event.target.value, 10) });
    }

    getType(typeId, typeList) {
        return _.find(typeList, (tp) => tp.id === typeId);
    }

    onSave() {
        API.saveExceptionObjectsForReservations(
            this.props.reservationIds,
            _.uniq(_.flatten(this.state.addExceptions.map((aE) => aE.types))), // Need to track all used lists, appending them if necessary
            this.state.addExceptions.map((aE) => aE.exception.id),
            this.state.removeExceptions.map((rE) => rE.id),
            (result) => {
                if (result[0] === false) {
                    Log.error(result[1][0].details);
                } else {
                    this.props.onClose();
                }
            }
        );
    }

    onCancel() {
        this.props.onClose();
    }

    renderButtons() {
        return (
            <div>
                <button className="deprecated modernButton" onClick={this.onCancel.bind(this)}>
                    {Language.get("dialog_cancel")}
                </button>
                <button className="deprecated modernButton" onClick={this.onSave.bind(this)}>
                    {Language.get("dialog_save")}
                </button>
            </div>
        );
    }

    render() {
        let childTypes: TMemberType[] = [];
        if (this.state.selectedGroupType && this.state.childMemberTypes.length > 0) {
            childTypes =
                this.state.childMemberTypes[
                    this.state.parentMemberTypes
                        .map((type) => type.id)
                        .indexOf(this.state.selectedGroupType)
                ];
        }
        const removeIds = this.state.removeExceptions.map((re) => re.id);
        const memberObjects = this.state.membershipObjects.map((obj) => obj.id).concat(removeIds);
        const excludeObjects = this.state.selectedObjects
            .map((obj) => obj.id)
            .filter((eO) => memberObjects.indexOf(eO) !== -1)
            .concat(this.state.exceptions.map((ex) => ex.id))
            .filter((id) => removeIds.indexOf(id) === -1);
        const availableObjects = _.uniq(
            memberObjects.filter((id) => excludeObjects.indexOf(id) === -1)
        );
        const buttons = <div className="buttons btnGroup horizontal">{this.renderButtons()}</div>;
        return (
            <div style={flexParent}>
                <div className="bottomPaddedSelects" style={verticalFlex}>
                    <select
                        onChange={this.onGroupTypeSelected.bind(this)}
                        value={this.state.selectedGroupType || ""}
                    >
                        <option>{Language.get("nc_reservation_exception_select_type")}</option>
                        {this.state.parentMemberTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                                {type.name}
                            </option>
                        ))}
                    </select>
                    <label>{Language.get("nc_reservation_exception_member_type_to_exclude")}</label>
                    <select
                        onChange={this.onMemberTypeSelected.bind(this)}
                        value={this.state.selectedMemberType || ""}
                    >
                        {childTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                                {type.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={horizontalFlex}>
                    <div style={leftList} className="objectSelectList">
                        <ObjectSelect
                            width={"100%"}
                            selectedType={this.getType(this.state.selectedMemberType, childTypes)}
                            onObjectSearcherChange={null}
                            selectedFluffyItem={null}
                            onClick={this.handleAddException.bind(this)}
                            onObjectInfo={null}
                            addReloadFunction={null}
                            ref="objectSelection"
                            onRowHover={null}
                            hoverButton={null}
                            modalKey={"allowSecondModal"}
                            highlightSelection={false}
                            reservationIds={this.props.reservationIds}
                            isStatic={true}
                            staticObjects={availableObjects}
                            user={this.props.user}
                        />
                    </div>
                    <div style={flexGrow} className="objectSelectList">
                        <label>{Language.get("nc_reservation_exception_excepted_members")}</label>
                        <br />
                        <ObjectSelect
                            width={"100%"}
                            selectedType={this.getType(this.state.selectedMemberType, childTypes)}
                            onObjectSearcherChange={null}
                            selectedFluffyItem={null}
                            onClick={this.handleRemoveException.bind(this)}
                            onObjectInfo={null}
                            addReloadFunction={null}
                            ref="objectSelection2"
                            onRowHover={null}
                            hoverButton={null}
                            modalKey={"allowSecondModal"}
                            reservationIds={this.props.reservationIds}
                            isStatic={true}
                            staticObjects={excludeObjects}
                            occuringObjects={this.state.selectedObjects}
                            disabled={this.state.removeAllOfType}
                            highlightSelection={true}
                            user={this.props.user}
                        />
                    </div>
                </div>
                {buttons}
            </div>
        );
    }
}

export default ExceptionEditor;
